import { mergeOptionsWithAuth } from '@agyletime/vue-3-common/src/utils/apiService';
import { useEnvironmentData } from '@agyletime/vue-3-common/src/composables/environmentData';
import { DEFAULT_PAGE_SIZE } from '../utils/constants';
import { MappedFacetObject } from '../types/facetsTypes';
import { mapFacetResponse } from '../mappers/facetMappers';
import { formatInTimeZone } from 'date-fns-tz';
import { getUserData } from '@/utils/userData';

const environmentData = await useEnvironmentData();

// Custom error class with status property
class HttpError extends Error {
    status: number;
    
    constructor(message: string, status: number) {
        super(message);
        this.name = 'HttpError';
        this.status = status;
    }
}

function calculateAge(startDate: string, timezone?: string): number {
    const start = new Date(startDate);
    const now = new Date();
    
    if (timezone) {
        const startInTz = formatInTimeZone(start, timezone, "yyyy-MM-dd'T'HH:mm:ss");
        const nowInTz = formatInTimeZone(now, timezone, "yyyy-MM-dd'T'HH:mm:ss");
        const startTzDate = new Date(startInTz);
        const nowTzDate = new Date(nowInTz);
        return Math.floor((nowTzDate.getTime() - startTzDate.getTime()) / (1000 * 60 * 60 * 24));
    }
    return Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
}

export async function mapTeamScheduleData(data: unknown[]): Promise<unknown[]> {
    const userDataTimezone = await getUserData(['timezone']);
    const timezone = userDataTimezone?.timezone;
    
    return data.map((item) => {
        const teamScheduleItem = item as Record<string, unknown>;
        
        const closedAt = teamScheduleItem.closedTime;
        const startDate = teamScheduleItem.startTime;
        
        let age = null;
        let status = 'Closed';
        
        if (!closedAt) {
            status = 'Open';
            if (startDate && typeof startDate === 'string') {
                age = calculateAge(startDate, timezone);
            }
        }
        
        return {
            ...teamScheduleItem,
            age,
            status
        };
    });
}

export interface PaginatedResponse {
    status?: string;
    itemsCount: number;
    pageLimit: number;
    data: unknown[];
}

export async function getPaginatedResponse(fetchUrl: string, pageLimit = DEFAULT_PAGE_SIZE, nextPageToken?: string): Promise<PaginatedResponse> {
    let url = `${fetchUrl}&pageLimit=${pageLimit}`;
    
    if (nextPageToken) {
        url += `&nextPageToken=${encodeURIComponent(nextPageToken)}`;
    }

    const response: PaginatedResponse = await fetchFromLambda(url);
    return response;
}

export async function getUnPaginatedResponse(fetchUrl: string, body: object): Promise<unknown> {
    const response: unknown = await fetchFromLambda(fetchUrl, {
        method: 'POST',
        body: JSON.stringify(body)
    });
    return response;
}

export async function getPaginatedTeamScheduleResponse(
    fetchUrl: string,
    pageLimit = DEFAULT_PAGE_SIZE,
    nextPageToken?: string
): Promise<PaginatedResponse> {
    const response = await getPaginatedResponse(fetchUrl, pageLimit, nextPageToken);
    
    if (response.data && Array.isArray(response.data)) {
        response.data = await mapTeamScheduleData(response.data);
    }
    
    return response;
}

export async function getTeamAndScheduleResponse(
    fetchUrl: string,
    pageLimit = DEFAULT_PAGE_SIZE,
    pageNumber?: string | number,
    body: object = {}
): Promise<unknown> {
    const updatedBody = {
        ...body,
        pageLimit,
        page: pageNumber
    };

    const response = await getUnPaginatedResponse(fetchUrl, updatedBody);
    return response;
}

export async function getFacets(fetchUrl: string): Promise<MappedFacetObject> {
    const url = `${fetchUrl}&onlyFacets=true`;
    const response = await fetchFromLambda(url);
    return mapFacetResponse(response);
}

export async function exportCSV(fetchUrl: string, activeColumns: string[]) {
    let url = `${fetchUrl}&isExport=true`;
    
    if (activeColumns && activeColumns.length > 0) {
        const columnsParam = activeColumns.join(',');
        url += `&columns=${encodeURIComponent(columnsParam)}`;
    }
    
    const response = await fetchFromLambda(url);
    return response;
}

const fetchFromLambda = (url: string, options?: { headers?: Record<string, string>; method?: string; body?: string }) => {
    const optionsWithAuth = mergeOptionsWithAuth(options);

    return fetch(`${environmentData.apiGatewayBaseUrl}${url}`, optionsWithAuth)
        .then((response) => {
            if (!response.ok) {
                throw new HttpError(response.statusText, response.status || 400);
            }
            if (response.status === 204) {
                return null;
            }
            return response.json();
        });
};